import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FaSave, FaTimes, FaUser, FaEnvelope, FaGraduationCap, FaInfoCircle } from 'react-icons/fa';
import { enquiryAPI } from '../../services/api';

const EnquiryForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    interested_course: '',
    preferred_batch_timing: '',
    how_did_you_hear: '',
    additional_notes: ''
  });

  const courseOptions = [
    { value: '', label: 'Select a course' },
    { value: 'Python Programming', label: 'Python Programming' },
    { value: 'Data Science', label: 'Data Science' },
    { value: 'Machine Learning', label: 'Machine Learning' },
    { value: 'Artificial Intelligence', label: 'Artificial Intelligence' }
  ];

  const timingOptions = [
    { value: '', label: 'Select preferred timing' },
    { value: 'Afternoon (2 PM - 5 PM)', label: 'Afternoon (2 PM - 5 PM)' },
    { value: 'Evening (6 PM - 8 PM)', label: 'Evening (6 PM - 8 PM)' },
    { value: 'Weekend', label: 'Weekend' },
    { value: 'Flexible', label: 'Flexible' }
  ];

  const sourceOptions = [
    { value: '', label: 'How did you hear about us?' },
    { value: 'Social Media', label: 'Social Media' },
    { value: 'Google Search', label: 'Google Search' },
    { value: 'Friend/Family Referral', label: 'Friend/Family Referral' },
    { value: 'Advertisement', label: 'Advertisement' },
    { value: 'Walk-in', label: 'Walk-in' },
    { value: 'QR Code', label: 'QR Code' },
    { value: 'Other', label: 'Other' }
  ];

  const fetchEnquiry = useCallback(async () => {
    try {
      setLoading(true);
      const response = await enquiryAPI.getById(id);
      const enquiry = response.data;
      
      setFormData({
        first_name: enquiry.first_name || '',
        last_name: enquiry.last_name || '',
        email: enquiry.email || '',
        phone: enquiry.phone || '',
        interested_course: enquiry.interested_course || '',
        preferred_batch_timing: enquiry.preferred_batch_timing || '',
        how_did_you_hear: enquiry.how_did_you_hear || '',
        additional_notes: enquiry.additional_notes || ''
      });
    } catch (error) {
      console.error('Error fetching enquiry:', error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (id) {
      fetchEnquiry();
    }
  }, [id, fetchEnquiry]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const validateForm = () => {
    const requiredFields = ['first_name', 'last_name', 'email', 'phone'];
    for (const field of requiredFields) {
      if (!formData[field].trim()) {
        return false;
      }
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      return false;
    }

    // Basic phone validation
    const phoneRegex = /^[+]?[1-9][\d]{0,15}$/;
    if (!phoneRegex.test(formData.phone.replace(/[\s\-()]/g, ''))) {
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      setSaving(true);
      
      if (id) {
        // Update existing enquiry
        await enquiryAPI.update(id, formData);
      } else {
        // Create new enquiry
        await enquiryAPI.create(formData);
      }
      
      navigate('/enquiries');
    } catch (error) {
      console.error('Error saving enquiry:', error);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 bg-gray-900 min-h-screen">
      <div className="max-w-4xl mx-auto">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold text-white">
            {id ? 'Edit Enquiry' : 'Add New Enquiry'}
          </h1>
          <button
            onClick={() => navigate('/enquiries')}
            className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors shadow-lg"
          >
            <FaTimes />
            Cancel
          </button>
        </div>

        <form onSubmit={handleSubmit} className="bg-gray-800 rounded-lg shadow-lg p-6 border border-gray-700">
          {/* Personal Information */}
          <div className="mb-8">
            <h2 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
              <FaUser className="text-blue-400" />
              Personal Information
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  First Name *
                </label>
                <input
                  type="text"
                  name="first_name"
                  value={formData.first_name}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-white placeholder-gray-400"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Last Name *
                </label>
                <input
                  type="text"
                  name="last_name"
                  value={formData.last_name}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-white placeholder-gray-400"
                  required
                />
              </div>
            </div>
          </div>

          {/* Contact Information */}
          <div className="mb-8">
            <h2 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
              <FaEnvelope className="text-blue-400" />
              Contact Information
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Email Address *
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-white placeholder-gray-400"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Phone Number *
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-white placeholder-gray-400"
                  required
                />
              </div>
            </div>
          </div>

          {/* Course Information */}
          <div className="mb-8">
            <h2 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
              <FaGraduationCap className="text-blue-400" />
              Course Information
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Interested Course
                </label>
                <select
                  name="interested_course"
                  value={formData.interested_course}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-white"
                >
                  {courseOptions.map(option => (
                    <option key={option.value} value={option.value} className="bg-gray-700 text-white">
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Preferred Batch Timing
                </label>
                <select
                  name="preferred_batch_timing"
                  value={formData.preferred_batch_timing}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-white"
                >
                  {timingOptions.map(option => (
                    <option key={option.value} value={option.value} className="bg-gray-700 text-white">
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Additional Information */}
          <div className="mb-8">
            <h2 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
              <FaInfoCircle className="text-blue-400" />
              Additional Information
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  How did you hear about us?
                </label>
                <select
                  name="how_did_you_hear"
                  value={formData.how_did_you_hear}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-white"
                >
                  {sourceOptions.map(option => (
                    <option key={option.value} value={option.value} className="bg-gray-700 text-white">
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Additional Notes
                </label>
                <textarea
                  name="additional_notes"
                  value={formData.additional_notes}
                  onChange={handleInputChange}
                  rows="4"
                  placeholder="Any additional information or specific requirements..."
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-white placeholder-gray-400"
                />
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex justify-end gap-4 pt-6 border-t border-gray-600">
            <button
              type="button"
              onClick={() => navigate('/enquiries')}
              className="bg-gray-600 hover:bg-gray-700 text-white px-6 py-2 rounded-lg transition-colors shadow-lg"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white px-6 py-2 rounded-lg flex items-center gap-2 transition-colors shadow-lg"
            >
              <FaSave />
              {saving ? 'Saving...' : (id ? 'Update Enquiry' : 'Create Enquiry')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EnquiryForm; 